import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbPlus, TbTrash, TbPencil, TbX, TbInfoCircle } from "react-icons/tb";
import PhoneInput from "src/components/PhoneInput/PhoneInput";
import { SpecialtyPicker } from "src/pages/Settings/SpecialtyPicker";
import { normalizeIban, validateIban } from "src/helpers/sanitizeInput";
import { PasswordInput } from "src/components/PasswordInput/PasswordInput";
import {
    RegisterFormData,
    MemberData,
    PLAN_MAX_MEMBERS,
    PLAN_MAX_DOCTORS,
    PLAN_MAX_STAFF,
    PLAN_NAMES,
    DOCTOR_ROLES,
    STAFF_ROLES,
    SALUTATION_OPTIONS,
    TITLE_OPTIONS,
    USER_ROLES,
} from "../registerTypes";

const SPECIALTY_ROLES = new Set([2, 3]); // Doctor=2, Admin=3

interface Props {
    data: RegisterFormData;
    setData: React.Dispatch<React.SetStateAction<RegisterFormData>>;
}

function newMember(): MemberData {
    return {
        _key:            crypto.randomUUID(),
        salutation:      "Herr",
        title:           "",
        firstName:       "",
        lastName:        "",
        specialty:       [],
        email:           "",
        password:        "",
        confirmPassword: "",
        phones:          [],
        iban:            "",
        bic:             "",
        taxNumber:       "",
        avatar:          "",
        userRights:      2,
    };
}

// ── Member form (shared between modal and inline) ─────────────────────────────

interface MemberFormProps {
    member:      MemberData;
    isAdmin:     boolean;
    allowedRoles?: number[]; // if set, restricts which roles can be chosen
    onChange:    (field: keyof MemberData, value: MemberData[keyof MemberData]) => void;
}

const MAX_IMG_BYTES = 5 * 1024 * 1024;

function MemberForm({ member, isAdmin, allowedRoles, onChange }: MemberFormProps) {
    const { t } = useTranslation();
    const avatarRef = useRef<HTMLInputElement>(null);
    const [ibanError, setIbanError] = useState<string | null>(null);
    const [avatarError, setAvatarError] = useState<string | null>(null);

    const isDoctorRole   = isAdmin || member.userRights === 2;
    const showSpecialty  = isAdmin || SPECIALTY_ROLES.has(member.userRights);
    const showIbanNotice = isDoctorRole && !member.iban.trim();

    const handleIbanBlur = (raw: string) => {
        const normalized = normalizeIban(raw);
        onChange("iban", normalized);
        if (normalized && !validateIban(normalized)) {
            setIbanError(t("register.errorIbanFormat"));
        } else {
            setIbanError(null);
        }
    };

    const addPhone = () =>
        onChange("phones", [...member.phones, { id: crypto.randomUUID(), value: "" }]);
    const removePhone = (id: string) =>
        onChange("phones", member.phones.filter((p: { id: string }) => p.id !== id));
    const updatePhone = (id: string, value: string) =>
        onChange("phones", member.phones.map((p: { id: string; value: string }) =>
            p.id === id ? { ...p, value } : p));

    const handleAvatar = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (file.size > MAX_IMG_BYTES) {
            setAvatarError(t("form.imageTooLarge"));
            return;
        }
        setAvatarError(null);
        const reader = new FileReader();
        reader.onload = () => onChange("avatar", reader.result as string);
        reader.readAsDataURL(file);
    };

    return (
        <div className="register-step">
            {/* Salutation + Title + Role */}
            <div className="register-step__row">
                <div className="form-group register-step__pct20">
                    <label>{t("register.salutation")}</label>
                    <select className="settings-select" value={member.salutation}
                        onChange={e => onChange("salutation", e.target.value)}>
                        {SALUTATION_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                <div className="form-group register-step__pct20">
                    <label>{t("register.memberTitle")}</label>
                    <select className="settings-select" value={isDoctorRole ? member.title : ""}
                        disabled={!isDoctorRole}
                        onChange={e => onChange("title", e.target.value)}>
                        {TITLE_OPTIONS.map(t_ => <option key={t_} value={t_}>{t_ || "—"}</option>)}
                    </select>
                </div>
                {isAdmin ? (
                    <div className="form-group register-step__pct60">
                        <label>{t("register.userRights")}</label>
                        <select className="settings-select" disabled value={3}>
                            <option value={3}>{t("register.roleAdmin")}</option>
                        </select>
                    </div>
                ) : (
                    <div className="form-group register-step__pct60">
                        <label>{t("register.userRights")}</label>
                        <select className="settings-select" value={member.userRights}
                            onChange={e => onChange("userRights", Number(e.target.value))}>
                            {USER_ROLES
                                .filter(r => r.value === 3 ? false : (!allowedRoles || allowedRoles.includes(r.value)))
                                .map(r => (
                                    <option key={r.value} value={r.value}>{t(r.labelKey)}</option>
                                ))}
                        </select>
                    </div>
                )}
            </div>

            {/* First name + Last name */}
            <div className="register-step__row">
                <div className="form-group register-step__half">
                    <label>{t("register.firstName")} *</label>
                    <input type="text" value={member.firstName}
                        onChange={e => onChange("firstName", e.target.value)} />
                </div>
                <div className="form-group register-step__half">
                    <label>{t("register.lastName")} *</label>
                    <input type="text" value={member.lastName}
                        onChange={e => onChange("lastName", e.target.value)} />
                </div>
            </div>

            {/* Email */}
            <div className="form-group">
                <label>{t("register.memberEmail")} *</label>
                <input type="email" value={member.email}
                    onChange={e => onChange("email", e.target.value)}
                    placeholder="z.B. arzt@praxis.de" />
            </div>

            {/* Password */}
            <div className="register-step__row">
                <div className="form-group register-step__half">
                    <label>{t("register.memberPassword")} *</label>
                    <PasswordInput
                        value={member.password}
                        onChange={e => onChange("password", e.target.value)}
                        placeholder="••••••••"
                        autoComplete="new-password"
                    />
                </div>
                <div className="form-group register-step__half">
                    <label>{t("register.confirmMemberPassword")} *</label>
                    <PasswordInput
                        value={member.confirmPassword}
                        onChange={e => onChange("confirmPassword", e.target.value)}
                        placeholder="••••••••"
                        autoComplete="new-password"
                    />
                </div>
            </div>
            <p className="register-step__password-hint">{t("register.passwordHint")}</p>

            {/* Specialty — only for Admin or Doctor */}
            {showSpecialty && (
                <div className="form-group register-step__specialty">
                    <label>{t("register.specialty")} *</label>
                    <SpecialtyPicker value={member.specialty}
                        onChange={v => onChange("specialty", v)} />
                </div>
            )}

            {/* Phones + Avatar */}
            <div className="register-step__row register-step__row--top">
                <div className="form-group register-step__half">
                    <label>{t("register.phones")}</label>
                    {member.phones.map((p: { id: string; value: string }) => (
                        <div key={p.id} className="register-step__phone-row">
                            <PhoneInput value={p.value} onChange={v => updatePhone(p.id, v)} />
                            <button type="button"
                                className="register-step__icon-btn register-step__icon-btn--danger"
                                onClick={() => removePhone(p.id)}>
                                <TbTrash />
                            </button>
                        </div>
                    ))}
                    <button type="button" className="register-step__add-btn" onClick={addPhone}>
                        <TbPlus /> {t("register.addPhone")}
                    </button>
                </div>

                <div className="form-group register-step__half">
                    <label>{t("register.avatar")}</label>
                    <div className="register-step__logo-area register-step__logo-area--sm"
                        onClick={() => avatarRef.current?.click()}>
                        {member.avatar
                            ? <img src={member.avatar} alt="" className="register-step__logo-preview" />
                            : <span className="register-step__logo-placeholder">{t("register.uploadPhoto")}</span>
                        }
                    </div>
                    <input ref={avatarRef} type="file" accept="image/*"
                        className="register-step__file-hidden" onChange={handleAvatar} />
                    {avatarError && <p className="avatar-upload__error">{avatarError}</p>}
                </div>
            </div>

            {/* IBAN / BIC / Tax — doctors and admins only */}
            {isDoctorRole && (<>
                <div className="form-group">
                    <label>{t("register.memberIban")}</label>
                    <input type="text" value={member.iban}
                        onChange={e => { onChange("iban", e.target.value.toUpperCase()); setIbanError(null); }}
                        onBlur={e => handleIbanBlur(e.target.value)}
                        placeholder="DE84 7507 0024..." />
                    {ibanError && <span className="register-step__field-error">{ibanError}</span>}
                    {showIbanNotice && (
                        <div className="register-member-info register-member-info--subtle">
                            <TbInfoCircle />
                            {t("register.ibanFallbackNotice")}
                        </div>
                    )}
                </div>
                <div className="register-step__row">
                    <div className="form-group register-step__half">
                        <label>{t("register.memberBic")}</label>
                        <input type="text" value={member.bic}
                            onChange={e => onChange("bic", e.target.value.toUpperCase())}
                            placeholder="DEUTDEFFXXX" />
                    </div>
                    <div className="form-group register-step__half">
                        <label>{t("register.memberTaxNumber")}</label>
                        <input type="text" value={member.taxNumber}
                            onChange={e => onChange("taxNumber", e.target.value)} />
                    </div>
                </div>
            </>)}
        </div>
    );
}

// ── Modal ─────────────────────────────────────────────────────────────────────

interface ModalProps {
    member:       MemberData;
    isAdmin:      boolean;
    isNew:        boolean;
    allowedRoles?: number[];
    onSave:       (m: MemberData) => void;
    onClose:      () => void;
}

function MemberModal({ member, isAdmin, isNew, allowedRoles, onSave, onClose }: ModalProps) {
    const { t } = useTranslation();
    const [draft, setDraft] = useState<MemberData>(member);

    const onChange = (field: keyof MemberData, value: MemberData[keyof MemberData]) =>
        setDraft(d => ({ ...d, [field]: value }));

    return (
        <div className="register-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="register-modal">
                <div className="register-modal__header">
                    <span className="register-modal__title">
                        {isNew ? t("register.addMember") : t("register.editMember")}
                    </span>
                    <button type="button" className="register-modal__close" onClick={onClose}>
                        <TbX />
                    </button>
                </div>
                <div className="register-modal__body">
                    <MemberForm member={draft} isAdmin={isAdmin} allowedRoles={allowedRoles} onChange={onChange} />
                </div>
                <div className="register-modal__footer">
                    <button type="button" className="register-back-btn" onClick={onClose}>
                        {t("register.cancel")}
                    </button>
                    <button type="button" className="btn-primary" onClick={() => onSave(draft)}>
                        {t("register.save")}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Compact member row ────────────────────────────────────────────────────────

interface MemberRowProps {
    member:  MemberData;
    isAdmin: boolean;
    onEdit:  () => void;
    onRemove: () => void;
}

function MemberRow({ member, isAdmin, onEdit, onRemove }: MemberRowProps) {
    const { t } = useTranslation();
    const initials = `${member.firstName?.[0] ?? ""}${member.lastName?.[0] ?? ""}`.toUpperCase() || "?";
    const displayName = [member.salutation, member.title, member.firstName, member.lastName].filter(Boolean).join(" ") || t("register.unnamedMember");
    const roleLabel = isAdmin
        ? t("register.roleAdmin")
        : t(USER_ROLES.find(r => r.value === member.userRights)?.labelKey ?? "register.roleDoctor");

    return (
        <div className={`register-member-row${isAdmin ? " register-member-row--admin" : ""}`}>
            <div className="register-member-row__avatar">
                {member.avatar
                    ? <img src={member.avatar} alt="" />
                    : <span>{initials}</span>
                }
            </div>
            <div className="register-member-row__info">
                <span className="register-member-row__name">{displayName}</span>
                <span className="register-member-row__role">{roleLabel}{member.email ? ` · ${member.email}` : ""}</span>
            </div>
            <div className="register-member-row__actions">
                <button type="button" className="register-step__icon-btn" onClick={onEdit} title={t("register.edit")}>
                    <TbPencil />
                </button>
                {!isAdmin && (
                    <button type="button" className="register-step__icon-btn register-step__icon-btn--danger"
                        onClick={onRemove} title={t("register.remove")}>
                        <TbTrash />
                    </button>
                )}
            </div>
        </div>
    );
}

// ── Main step ─────────────────────────────────────────────────────────────────

export default function StepMembers({ data, setData }: Props) {
    const { t } = useTranslation();
    const planName   = PLAN_NAMES[data.plan] ?? "";
    const maxDoctors = PLAN_MAX_DOCTORS[data.plan] ?? 1;
    const maxStaff   = PLAN_MAX_STAFF[data.plan]   ?? 0;

    // Count current doctors and staff (admin at index 0 always counts as a doctor)
    const doctorCount = data.members.filter((m, i) => i === 0 || DOCTOR_ROLES.has(m.userRights)).length;
    const staffCount  = data.members.filter((m, i) => i > 0  && STAFF_ROLES.has(m.userRights)).length;

    const canAddDoctor = doctorCount < maxDoctors;
    const canAddStaff  = staffCount  < maxStaff;
    const canAddAny    = canAddDoctor || canAddStaff;

    // When adding, only offer roles that still have slots
    const allowedRolesForNew: number[] = [
        ...(canAddDoctor ? [2] : []),   // Doctor
        ...(canAddStaff  ? [1, 5] : []), // Medical Assistant, Accounting
    ];

    // editIdx: null = closed, -1 = adding new, ≥0 = editing that index
    const [editIdx, setEditIdx] = useState<number | null>(null);
    const [draft,   setDraft]   = useState<MemberData | null>(null);

    // Allowed roles when editing an existing member (preserve current role + available slots)
    const allowedRolesForEdit = (idx: number): number[] => {
        const m = data.members[idx];
        const isCurrentDoctor = DOCTOR_ROLES.has(m.userRights);
        const isCurrentStaff  = STAFF_ROLES.has(m.userRights);
        // Can keep same category + switch to the other if there's room
        return [
            ...(isCurrentDoctor || canAddDoctor ? [2] : []),
            ...(isCurrentStaff  || canAddStaff  ? [1, 5] : []),
        ];
    };

    const openAdd  = () => {
        const m = newMember();
        // Default role to first available
        if (!canAddDoctor && canAddStaff) m.userRights = 1;
        setDraft(m);
        setEditIdx(-1);
    };
    const openEdit   = (idx: number) => { setDraft({ ...data.members[idx] }); setEditIdx(idx); };
    const closeModal = () => { setEditIdx(null); setDraft(null); };

    const handleSave = (saved: MemberData) => {
        setData(d => {
            const members = [...d.members];
            if (editIdx === -1) members.push(saved);
            else if (editIdx !== null) members[editIdx] = saved;
            return { ...d, members };
        });
        closeModal();
    };

    const removeMember = (idx: number) =>
        setData(d => ({ ...d, members: d.members.filter((_, i) => i !== idx) }));

    // Used in the modal — for new members use allowedRolesForNew, for existing use per-member allowed
    const modalAllowedRoles = editIdx === -1
        ? allowedRolesForNew
        : (editIdx !== null ? allowedRolesForEdit(editIdx) : undefined);

    return (
        <div className="register-step">
            {/* Info banners */}
            <div className="register-members-notices">
                <div className="register-member-info">
                    <TbInfoCircle />
                    <span>
                        {t("register.planDoctorInfo", { plan: planName, maxDoctors, doctorCount })}
                        {maxStaff > 0 && ` · ${t("register.planStaffInfo", { maxStaff, staffCount })}`}
                    </span>
                </div>
                <div className="register-member-info">
                    <TbInfoCircle />
                    <span>{t("register.ibanFallbackInfo")}</span>
                </div>
                {!canAddAny && (
                    <div className="register-member-info">
                        <TbInfoCircle />
                        <span>{t("register.memberOverageInfo")}</span>
                    </div>
                )}
            </div>

            {/* Member list */}
            <div className="register-members-list">
                {data.members.map((m, i) => (
                    <MemberRow
                        key={m._key}
                        member={m}
                        isAdmin={i === 0}
                        onEdit={() => openEdit(i)}
                        onRemove={() => removeMember(i)}
                    />
                ))}
            </div>

            {/* Admin name missing warning */}
            {data.members.length > 0 && (!data.members[0].firstName.trim() || !data.members[0].lastName.trim()) && (
                <div className="register-member-info register-member-info--warn">
                    <TbInfoCircle />
                    <span>{t("register.adminNameMissing")}</span>
                </div>
            )}

            {/* Add member — hidden on BASIC plan (only 1 doctor slot, no staff) */}
            {data.plan !== 1 && (
                <button
                    type="button"
                    className="register-step__add-btn register-step__add-btn--member"
                    onClick={canAddAny ? openAdd : undefined}
                    disabled={!canAddAny}
                    title={!canAddAny ? t("register.planLimitReached") : undefined}
                >
                    <TbPlus /> {t("register.addMember")}
                </button>
            )}

            {/* "Add more members later" note */}
            <p className="register-members-add-later">{t("register.addLaterNote")}</p>

            {/* Modal */}
            {editIdx !== null && draft && (
                <MemberModal
                    member={draft}
                    isAdmin={editIdx === 0}
                    isNew={editIdx === -1}
                    allowedRoles={modalAllowedRoles}
                    onSave={handleSave}
                    onClose={closeModal}
                />
            )}
        </div>
    );
}
